import React, { useEffect, useMemo, useState } from 'react';
import { IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import styles from './diagnoses.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import DateChip from '../moduleCard/DateChip';
import Toast from '../../../../components/support/Toast';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import IcdSearch from './IcdSearch';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildDiagnosesJob } from '../../modules/reportSections';
import {
  CLASSIFICATIONS, DEFAULT_CLASSIFICATION, MAX_PER_APPOINTMENT, attributionsFor, classificationOf, groupByAppointment, isFromAppointment,
} from '../../modules/diagnoses/diagnoses';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.DIAGNOSES];

/** Diagnóstico como chip: classificação, código em destaque, título oficial e, se puder, o "x". */
const DiagnosisChip = ({ diagnosis, onRemove }) => (
  <span className={`${parts.chip} ${styles.diagnosis}`}>
    <span className={styles.version}>{classificationOf(diagnosis).label}</span>
    <span className={styles.code}>{diagnosis.code}</span>
    <span>{diagnosis.title}</span>
    {onRemove && (
      <IconButton size="small" className={styles.remove} aria-label={`Remover ${diagnosis.code}`} onClick={() => onRemove(diagnosis)}>
        <CloseIcon fontSize="inherit" />
      </IconButton>
    )}
  </span>
);

/**
 * Módulo "Diagnósticos (CID)": busca na CID-10 (padrão) ou na CID-11 oficiais
 * e grava os diagnósticos escolhidos no atendimento, cada um com a sua
 * classificação; os de consultas anteriores ficam listados por data.
 *
 * `data`: diagnósticos do paciente (de qualquer atendimento).
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const Diagnoses = ({
  data,
  patientId,
  appointmentId,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const { SetPatientDiagnosis, DeletePatientDiagnosis } = DataProviderInstance();
  const [records, setRecords] = useState(Array.isArray(data) ? data : []);
  const [classification, setClassification] = useState(DEFAULT_CLASSIFICATION);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setRecords(Array.isArray(data) ? data : []);
  }, [data]);

  const current = useMemo(
    () => (appointmentId ? records.filter((item) => isFromAppointment(item, appointmentId)) : []),
    [records, appointmentId]
  );
  const previous = useMemo(
    () => groupByAppointment(appointmentId ? records.filter((item) => !isFromAppointment(item, appointmentId)) : records),
    [records, appointmentId]
  );
  const canEdit = enabled && !!appointmentId;
  const full = current.length >= MAX_PER_APPOINTMENT;

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildDiagnosesJob(records, appointmentId),
  });

  function add(option) {
    if (saving) return;
    setSaving(true);
    SetPatientDiagnosis(patientId, appointmentId, classification, option.code, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS' && response.data) {
        const saved = response.data;
        setRecords((list) => (list.some((item) => String(item.id) === String(saved.id)) ? list : [saved, ...list]));
        if (onUpdated) onUpdated();
      } else {
        setToast(response?.error || 'Não foi possível adicionar o diagnóstico.');
      }
    });
  }

  function remove(diagnosis) {
    DeletePatientDiagnosis(diagnosis.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        setRecords((list) => list.filter((item) => String(item.id) !== String(diagnosis.id)));
        if (onUpdated) onUpdated();
      } else {
        setToast('Não foi possível remover o diagnóstico.');
      }
    });
  }

  const actions = records.length > 0 ? <ModulePdfButton report={report} /> : null;

  return (
    <>
      <ModuleCard
        accent="pink"
        title="Diagnósticos (CID)"
        icon={<LocalHospitalOutlinedIcon />}
        description="Diagnósticos da consulta pela CID-10 ou pela CID-11"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {canEdit && (
            <div className={parts.section}>
              <span className={parts.sectionLabel}>Deste atendimento</span>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={classification}
                onChange={(event, value) => value && setClassification(value)}
                aria-label="Classificação"
                sx={{ alignSelf: 'flex-start' }}
              >
                {CLASSIFICATIONS.map((option) => (
                  <ToggleButton key={option.key} value={option.key} className={parts.toggleButton}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <IcdSearch
                classification={classification}
                onSelect={add}
                excludeCodes={current.map((item) => item.code)}
                disabled={saving || full}
              />
              {current.length > 0 ? (
                <div className={parts.chips}>
                  {current.map((diagnosis) => (
                    <DiagnosisChip key={diagnosis.id} diagnosis={diagnosis} onRemove={remove} />
                  ))}
                </div>
              ) : (
                <span className={parts.muted}>Nenhum diagnóstico neste atendimento.</span>
              )}
            </div>
          )}

          {previous.length > 0 && (
            <div className={parts.section}>
              {canEdit && <span className={parts.sectionLabel}>Atendimentos anteriores</span>}
              <div className={parts.scrollList}>
                {previous.map((group) => (
                  <div key={group.appointmentId} className={parts.item}>
                    <div className={parts.itemRow}>
                      <DateChip label={group.date} compact />
                    </div>
                    <div className={parts.chips}>
                      {group.items.map((diagnosis) => <DiagnosisChip key={diagnosis.id} diagnosis={diagnosis} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!canEdit && previous.length === 0 && (
            <ModuleEmptyState icon={<LocalHospitalOutlinedIcon />} text="Nenhum diagnóstico registrado." />
          )}

          {attributionsFor(records, canEdit ? classification : null).map((text) => (
            <span key={text} className={styles.attribution}>{text}</span>
          ))}
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {toast && (
        <Toast open message={toast} severity="error" onClose={() => setToast(null)} autoHideDuration={5000} />
      )}
    </>
  );
};

export default Diagnoses;
